import { useState } from "react";
import { toast } from "sonner";
import { Instagram, Music2, Mail, ArrowUpRight, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/portfolio/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { SITE, SERVICES } from "@/lib/site";

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", brand: "", website: "", service: "", details: "", budget: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent — I'll get back to you within 24 hours.");
      setForm({ name: "", email: "", brand: "", website: "", service: "", details: "", budget: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const field = "mt-2 bg-transparent border-0 border-b border-cream-deeper text-ink rounded-none h-12 px-0 focus-visible:ring-0 focus-visible:border-ink transition-colors placeholder:text-ink-soft/50";
  const label = "eyebrow text-ink-soft";

  return (
    <section id="contact" className="py-20 md:py-32 border-t border-cream-dark" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="eyebrow text-ink-soft mb-6">07 / Let's Talk</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-ink" data-testid="contact-headline">
              Ready to Create?
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed max-w-sm">
              Tell me about your brand, product, and what you're looking to create.
            </p>
            <div className="mt-10 space-y-4">
              <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-ink hover:text-ink-soft transition-colors duration-300">
                <Mail className="w-4 h-4" /> {SITE.email}
              </a>
              <a href={SITE.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="flex items-center gap-3 text-ink hover:text-ink-soft transition-colors duration-300">
                <Instagram className="w-4 h-4" /> Instagram — {SITE.instagram}
              </a>
              {SITE.tiktok && (
                <a href={SITE.tiktokUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok-link" className="flex items-center gap-3 text-ink hover:text-ink-soft transition-colors duration-300">
                  <Music2 className="w-4 h-4" /> TikTok — {SITE.tiktok}
                </a>
              )}
            </div>
            {SITE.whatsapp && (
              <a
                href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(SITE.whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="mt-8 inline-flex items-center gap-2.5 border border-ink text-ink px-7 py-3.5 rounded-full text-xs uppercase tracking-[0.14em] font-semibold hover:bg-ink hover:text-cream transition-colors duration-300"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
            )}
          </Reveal>
        </div>

        <Reveal className="lg:col-span-8" delay={0.1}>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
              <div>
                <Label htmlFor="cf-name" className={label}>Name</Label>
                <Input id="cf-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")}
                  className={field} placeholder="Your name" />
              </div>
              <div>
                <Label htmlFor="cf-brand" className={label}>Brand / Company</Label>
                <Input id="cf-brand" data-testid="contact-brand-input" value={form.brand} onChange={set("brand")}
                  className={field} placeholder="Company name" />
              </div>
              <div>
                <Label htmlFor="cf-email" className={label}>Email</Label>
                <Input id="cf-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")}
                  className={field} placeholder="you@company.com" />
              </div>
              <div>
                <Label htmlFor="cf-website" className={label}>Website / Social Media</Label>
                <Input id="cf-website" data-testid="contact-website-input" value={form.website} onChange={set("website")}
                  className={field} placeholder="https://" />
              </div>
            </div>
            <div>
              <Label className={label}>What do you need?</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="contact-service-select" className="mt-2 bg-transparent border-0 border-b border-cream-deeper rounded-none h-12 px-0 focus:ring-0 text-ink">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.title} value={s.title} data-testid={`service-option-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s.title}</SelectItem>
                  ))}
                  <SelectItem value="Something else" data-testid="service-option-something-else">Something else</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cf-details" className={label}>Message</Label>
              <Textarea id="cf-details" data-testid="contact-details-input" required rows={4} value={form.details} onChange={set("details")}
                className="mt-2 bg-transparent border-0 border-b border-cream-deeper text-ink rounded-none px-0 focus-visible:ring-0 focus-visible:border-ink transition-colors placeholder:text-ink-soft/50" placeholder="Tell me what you are building…" />
            </div>
            <div>
              <Label htmlFor="cf-budget" className={label}>Budget <span className="normal-case tracking-normal font-normal text-ink-soft/60">(optional)</span></Label>
              <Input id="cf-budget" data-testid="contact-budget-input" value={form.budget} onChange={set("budget")}
                className={field} placeholder="e.g. ₹15,000–₹30,000" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="text-sm text-ink-soft/70">I'll review your brief and get back to you with next steps.</p>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 bg-ink hover:bg-neutral-800 disabled:opacity-60 text-cream px-8 py-4 rounded-full text-xs uppercase tracking-[0.14em] font-semibold transition-colors duration-300"
              >
                {sending ? "Sending…" : "Let's Work Together"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-ink text-cream border-t border-cream/10 py-10 pb-24 md:pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <p className="font-serif text-2xl">{SITE.name}</p>
          <p className="mt-1 text-sm text-cream/60">© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="text-sm text-cream/70 hover:text-white transition-colors">{SITE.email}</a>
          <a href={SITE.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="text-cream/70 hover:text-white transition-colors">
            <Instagram className="w-5 h-5" />
          </a>
          {SITE.tiktok && (
            <a href={SITE.tiktokUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" aria-label="TikTok" className="text-cream/70 hover:text-white transition-colors">
              <Music2 className="w-5 h-5" />
            </a>
          )}
          <a
            href={`mailto:${SITE.email}?subject=Media%20Kit%20Request`}
            data-testid="footer-media-kit"
            className="text-xs uppercase tracking-[0.16em] font-semibold border border-cream/30 px-4 py-2 hover:bg-cream hover:text-ink transition-colors duration-300"
          >
            Download Media Kit
          </a>
        </div>
      </div>
    </footer>
  );
}
